import React from "react"
import Link from "next/link"
import { DownloadIcon, TrashIcon } from "lucide-react"

import { formatDate } from "@/lib/helper"
import { WishListWithId } from "@/lib/storefront/account/wishlist-service"
import { OrderLineItemData } from "@/lib/storefront/order/order"
import { ReducedProductData } from "@/lib/storefront/product/product"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/components"
import { QuadProductImagesPreview } from "@/components/ui/components/quad-product-images-preview"
import ReorderAllButtonClient from "@/components/account/orders/detail/ReorderAllButtonClient"

export function AccountWishlistsMiniCard(props: {
  productSkus: string[]
  productsAsync?: Promise<(ReducedProductData | undefined)[]>
  products?: ReducedProductData[]
  productItemsCountLabel: string
  isoLocale: string
  sbLocale: string
  wishlist: WishListWithId
  onRemove?: (id: string) => void
  showReorderButton?: boolean
  /** only with no outer <a> */
  showPDFLink?: boolean
  reorderButtonLabelBuyAgain?: string | undefined
  reorderButtonLabelIsAdding?: string | undefined
  reorderButtonLineItems?: OrderLineItemData[]
  pdfLinkUrl?: string | undefined
}) {
  return (
    <div className={"grid grid-cols-[4.25rem,_auto,_1rem] gap-x-2 px-2 xs:gap-x-4"}>
      <div
        className={cn("col-span-full grid grid-cols-subgrid", {
          "border-b border-gray-200 pb-2": props.showReorderButton || props.showPDFLink,
        })}
      >
        <QuadProductImagesPreview
          className={"relative top-1.5"}
          productSkus={props.productSkus}
          products={props.products}
          productsAsync={props.productsAsync}
        />
        <div className={"md:text-base/1 flex flex-col gap-0.5 text-sm/5 sm:text-base"}>
          <p>
            <span className={"text-red-600"}>{props.wishlist.title}</span>
          </p>
          <p>
            {formatDate(props.isoLocale, new Date(props.wishlist?.creationDate ?? 0), {
              day: "2-digit",
              month: "long",
              year: "numeric",
            })}
          </p>
          <p>
            <span className={"font-bold"}>{props.wishlist?.itemsCount ?? ""}</span>{" "}
            <span className={"font-light"}>{props.productItemsCountLabel}</span>
          </p>
        </div>
        <div className={cn("flex h-full w-fit flex-col justify-center", { "justify-between": props.onRemove })}>
          <div className={"h-8 w-fit overflow-hidden"}>
            <span className={"relative -top-6 inline-block text-[4rem] font-extralight leading-none text-primary"}>
              {"›"}
            </span>
          </div>
          {props.onRemove && (
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                props.onRemove && props.onRemove(props.wishlist.id)
              }}
              className={"text-gray-600 hover:text-primary"}
            >
              <TrashIcon className={"inline size-5"} />
            </button>
          )}
        </div>
      </div>
      <div className={"col-span-full flex justify-end gap-2 pt-2"}>
        {props.showPDFLink && (
          <Link
            prefetch={false}
            target={"_blank"}
            href={(props.pdfLinkUrl ?? "/bestellung-pdf?WishlistID=") + props.wishlist.id}
          >
            <Button size={"smNoGrow"} variant={"outline"} className="bg-primary text-white">
              <DownloadIcon className={"size-5"} />
            </Button>
          </Link>
        )}
        {props.showReorderButton && props.reorderButtonLineItems != undefined && (
          <ReorderAllButtonClient
            itemsSync={props.reorderButtonLineItems}
            label_buy_again={props.reorderButtonLabelBuyAgain ?? ""}
            label_is_adding={props.reorderButtonLabelIsAdding ?? ""}
            sbLocale={props.sbLocale}
          />
        )}
      </div>
    </div>
  )
}
